if (customElements.get('sticky-atc-v2') === undefined) {
  class StickyAtcV2 extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.updateStickyOffset = this.updateStickyOffset.bind(this);
      this.onDocChange = this.onDocChange.bind(this);
      this.onCtaClick = this.onCtaClick.bind(this);
      this.ticking = false;
    }

    connectedCallback() {
      const productForm = document.querySelector('product-form, .product-form');
      this.mainAtc =
        (productForm && productForm.querySelector('[type="submit"][name="add"], .product-form__submit')) ||
        document.querySelector('.product-form__submit, [name="add"], .add-to-cart');
      if (!this.mainAtc) {
        if (typeof console !== 'undefined') console.warn('[sticky-atc-v2] no product-form ATC found — sticky bar will not show/hide on scroll');
        return;
      }

      this.shadeMap = {};
      try {
        this.shadeMap = JSON.parse(this.dataset.shadeMap || '{}');
      } catch (e) {
        // Malformed shade-map breaks sticky bar swatch updates — promote to error
        // so the silent UX failure (sticky swatch stays on initial color) is visible.
        if (typeof console !== 'undefined') console.error('[sticky-atc-v2] shade-map is malformed JSON — sticky swatch updates disabled:', e, this.dataset.shadeMap);
      }

      this.single = this.querySelector('[data-sticky-mode="single"]');
      this.multi = this.querySelector('[data-sticky-mode="multi"]');
      this.swatchEl = this.querySelector('[data-sticky-swatch]');
      this.shadeNameEl = this.querySelector('[data-sticky-shade-name]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.ctaBtn = this.querySelector('[data-sticky-cta]');
      this.titleEl = this.querySelector('.sticky-atc-v2__title');
      this.baseTitle = this.titleEl ? this.titleEl.textContent.trim() : '';

      this.headerEl =
        document.querySelector('sticky-header.header-wrapper') ||
        document.querySelector('.header-wrapper') ||
        document.querySelector('.shopify-section-header');

      this.updateStickyOffset();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.updateStickyOffset);

      this.io = new IntersectionObserver(
        ([entry]) => {
          this.classList.toggle('is-visible', !entry.isIntersecting);
          this.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
        },
        { threshold: 0 }
      );
      this.io.observe(this.mainAtc);

      document.addEventListener('change', this.onDocChange);
      if (this.ctaBtn) this.ctaBtn.addEventListener('click', this.onCtaClick);

      this.syncMode();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.updateStickyOffset);
      document.removeEventListener('change', this.onDocChange);
      if (this.ctaBtn) this.ctaBtn.removeEventListener('click', this.onCtaClick);
      if (this.io) this.io.disconnect();
    }

    onDocChange(event) {
      const t = event.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (t.matches('input[name="concealer-amount"]')) {
        this.syncMode();
      } else if (t.matches('input[name="purchase_option"]')) {
        this.updatePrice();
      } else if (t.name && t.name.indexOf('bundleItem[') === 0) {
        if (this.multi && !this.multi.hidden) this.updateMulti();
      } else if (
        t.classList.contains('swatch-input__input') ||
        t.closest('variant-selects, [id^="concealer-variant-buy-1--"]')
      ) {
        if (this.single && !this.single.hidden) this.updateSingle();
      }
    }

    onCtaClick() {
      if (this.mainAtc) this.mainAtc.click();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.updateStickyOffset();
        this.ticking = false;
      });
    }

    updateStickyOffset() {
      if (!this.headerEl) return;
      const bottom = Math.max(0, this.headerEl.getBoundingClientRect().bottom);
      this.style.setProperty('--sticky-atc-v2-offset', bottom + 'px');
    }

    normalizeHex(value) {
      if (!value) return '';
      const v = String(value).trim();
      return v.startsWith('#') ? v : '#' + v;
    }

    moneyFormat(cents) {
      const n = parseInt(cents, 10);
      if (isNaN(n)) return '';
      // Use Shopify.formatMoney so the currency symbol respects shop locale
      // (US store renders $, UK store renders £, etc. — no hardcoded symbol).
      // Strip trailing .00 / ,00 to match Liquid's money_without_trailing_zeros filter.
      const formatted = (window.Shopify && Shopify.formatMoney) ? Shopify.formatMoney(n) : '$' + (n / 100).toFixed(2);
      return formatted.replace(/([.,])00(?!\d)/, '');
    }

    getCheckedAmountRadio() {
      return document.querySelector('input[name="concealer-amount"]:checked');
    }

    getCheckedPurchaseOption() {
      return document.querySelector('input[name="purchase_option"]:checked');
    }

    getCheckedBundleRadio(slot) {
      return document.querySelector('input[name="bundleItem[' + slot + ']"]:checked');
    }

    getCheckedBuyOneShade() {
      const root =
        document.querySelector('[id^="concealer-variant-buy-1--"]') ||
        document.querySelector('variant-selects');
      if (!root) return null;
      return root.querySelector('.swatch-input__input:checked, input[type="radio"]:checked');
    }

    updatePrice() {
      const r = this.getCheckedAmountRadio();
      if (!r) return;
      let cents = parseInt(r.dataset.price, 10);
      if (isNaN(cents)) return;

      const po = this.getCheckedPurchaseOption();
      if (po) {
        const pct = parseFloat(po.dataset.discountPercentage || '0');
        if (pct > 0) {
          cents = Math.round((cents * (100 - pct)) / 100);
        }
      }

      const formatted = this.moneyFormat(cents);
      if (formatted && this.priceEl) this.priceEl.textContent = formatted;
    }

    updateTitle() {
      if (!this.titleEl) return;
      const r = this.getCheckedAmountRadio();
      const qty = r ? parseInt(r.value, 10) : 1;
      this.titleEl.textContent = qty > 1 ? this.baseTitle + ' x ' + qty : this.baseTitle;
    }

    setSwatchAndName(swatchTarget, nameTarget, hex, title) {
      const normalized = this.normalizeHex(hex);
      if (swatchTarget && normalized) swatchTarget.style.background = normalized;
      if (nameTarget && title) nameTarget.textContent = title;
    }

    updateSingle() {
      const r = this.getCheckedBuyOneShade();
      if (!r) return;
      const title = r.dataset.shadeTitle || r.value || '';
      const hex = r.dataset.shadeHex || this.shadeMap[title] || '';
      this.setSwatchAndName(this.swatchEl, this.shadeNameEl, hex, title);
    }

    updateMulti() {
      for (let i = 1; i <= 3; i++) {
        const r = this.getCheckedBundleRadio(i);
        if (!r) continue;
        const title = r.dataset.shadeTitle || r.value || '';
        const hex = r.dataset.shadeHex || this.shadeMap[title] || '';
        const slotSwatch = this.querySelector('[data-sticky-multi-swatch="' + i + '"]');
        const slotName = this.querySelector('[data-sticky-multi-name="' + i + '"]');
        this.setSwatchAndName(slotSwatch, slotName, hex, title);
      }
    }

    syncMode() {
      const r = this.getCheckedAmountRadio();
      const isMulti = r ? parseInt(r.value, 10) > 1 : false;
      if (this.single) this.single.hidden = isMulti;
      if (this.multi) this.multi.hidden = !isMulti;
      this.updatePrice();
      this.updateTitle();
      if (isMulti) this.updateMulti();
      else this.updateSingle();
    }
  }

  customElements.define('sticky-atc-v2', StickyAtcV2);
}
